import { emperor } from '~/services';
import { UserMin } from '~/types/user';

export type PlaceCategory = 'food' | 'coffee' | 'outdoor' | 'shop';

export type Place = {
  uuid: string;
  building: string;
  name: string;
  category: PlaceCategory;
  /** Google Maps link for this place, when provided instead of a street address. */
  mapsLink?: string;
  street?: string;
  city?: string;
  zip?: string;
  description?: string;
  /** Resident visibility is derived from this: visible === !isClosed */
  isClosed: boolean;
  addedByName?: string;
  createdAt: string;
  reportCount?: number;
  likeCount?: number;
  beenCount?: number;
  commentCount?: number;
  shareCount?: number;
};

export type CreatePlacePayload = {
  name: string;
  category: PlaceCategory;
  mapsLink?: string;
  street?: string;
  city?: string;
  zip?: string;
  description?: string;
};

export type UpdatePlacePayload = Partial<
  Pick<
    Place,
    'name' | 'category' | 'street' | 'city' | 'zip' | 'description' | 'isClosed'
  >
>;

export const fetchPlaces = async (buildingId: string): Promise<Place[]> => {
  const res = await emperor.get(`/building/${buildingId}/place`);
  return res.data;
};

export const createPlace = async (
  buildingId: string,
  payload: CreatePlacePayload
): Promise<Place> => {
  const res = await emperor.post(`/building/${buildingId}/place`, payload);
  return res.data;
};

export const updatePlace = async (
  buildingId: string,
  placeId: string,
  patch: UpdatePlacePayload
): Promise<Place> => {
  const res = await emperor.patch(`/place/${placeId}`, patch);
  return res.data;
};

export type PlaceComment = {
  uuid: string;
  place: string;
  placeName: string;
  placeCategory: PlaceCategory;
  contents: string;
  hidden: boolean;
  hiddenBy?: string;
  user: UserMin;
  authorName: string;
  createdAt: string;
  myReaction?: string;
  reactionsCount?: number;
  byReactionUserIds?: Record<string, string[]>;
};

export type PlaceCommentsResponse = {
  comments: PlaceComment[];
  countsByCategory: Partial<Record<PlaceCategory, number>>;
  /** Set when a category is requested. */
  hasMore?: boolean;
};

// Pass `category` to page through that category's comments; omit it to fetch
// only `countsByCategory` (cheap — used for the PLACES sidebar badges).
export const fetchPlaceComments = async (
  buildingId: string,
  category?: PlaceCategory,
  page = 1
): Promise<PlaceCommentsResponse> => {
  const res = await emperor.get(`/building/${buildingId}/place-comment`, {
    params: category ? { category, page } : {}
  });
  return res.data;
};

export const updatePlaceComment = async (
  commentId: string,
  hidden: boolean
): Promise<PlaceComment> => {
  const res = await emperor.patch(`/place-comment/${commentId}`, { hidden });
  return res.data;
};

export const reactToPlaceComment = async (
  commentId: string,
  reactionName: string
): Promise<{ reactionsCount: number; myReaction: string | null }> => {
  const res = await emperor.post(`/place-comment/${commentId}/react`, {
    reactionName
  });
  return res.data;
};

export const unreactToPlaceComment = async (
  commentId: string
): Promise<{ reactionsCount: number; myReaction: string | null }> => {
  const res = await emperor.delete(`/place-comment/${commentId}/react`);
  return res.data;
};
